import { useEffect, useState } from 'react';
import { ArrowLeft, ClipboardCheck, Pencil, Save, Search } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import ordemProducaoService, { type OrdemProducao, type OrdemProducaoInput, type OrdemProducaoStatus } from '@/services/ordemProducao';
import produtoService, { type Produto } from '@/services/produto';
import responsavelService, { type Responsavel } from '@/services/responsavel';
import { getUuidCandidate, isUuid } from '@/utils/uuid';
import type { Tone } from '@/Components/ui/types';

const FORM_VAZIO: OrdemProducaoInput = { numero: '', produto_id: '', quantidade_planejada: 0, lote_producao: '', data_inicio_planejada: '', data_fim_planejada: '', responsavel_id: '', observacoes: '' };

const STATUS_LABELS: Record<OrdemProducaoStatus, string> = {
  ABERTA: 'Aberta',
  LIBERADA: 'Liberada',
  EM_PRODUCAO: 'Em Produção',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};

const STATUS_TONE: Record<OrdemProducaoStatus, Tone> = {
  ABERTA: 'neutral',
  LIBERADA: 'info',
  EM_PRODUCAO: 'warning',
  PAUSADA: 'purple',
  ENCERRADA: 'success',
  CANCELADA: 'danger',
};

type ApiValidationResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

const produtoLabel = (produto: Produto) => `${produto.codigo || '-'} - ${produto.descricao || ''}`;

const fieldLabels: Record<string, string> = {
  numero: 'Número',
  produto_id: 'Produto',
  quantidade_planejada: 'Quantidade planejada',
  lote_producao: 'Lote produção',
  data_inicio_planejada: 'Início planejado',
  data_fim_planejada: 'Fim planejado',
  responsavel_id: 'Responsável',
  observacoes: 'Observações',
};

function extractErrorMessages(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiValidationResponse | Record<string, unknown> | undefined;
    if (data && 'errors' in data && data.errors && typeof data.errors === 'object') {
      return Object.entries(data.errors as Record<string, string[]>).flatMap(([field, messages]) =>
        messages.map((message) => `${fieldLabels[field] || field}: ${message}`),
      );
    }
    if (data && 'message' in data && typeof data.message === 'string') return [data.message];
    if (data && typeof data === 'object') {
      const topLevelErrors = Object.entries(data).flatMap(([field, value]) => {
        if (!Array.isArray(value)) return [];
        return value.map((message) => `${fieldLabels[field] || field}: ${message}`);
      });
      if (topLevelErrors.length > 0) return topLevelErrors;
    }
  }

  return ['Não foi possível salvar a ordem de produção. Verifique os dados e tente novamente.'];
}

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatCurrency(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

type ProductAutocompleteFieldProps = {
  id?: string;
  produtos: Produto[];
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
};

function ProductAutocompleteField({
  id,
  produtos,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Buscar produto...',
}: ProductAutocompleteFieldProps) {
  const [open, setOpen] = useState(false);
  const selectedProduto = produtos.find((produto) => getUuidCandidate(produto) === value);
  const [text, setText] = useState('');
  const termo = text.trim().toLowerCase();

  useEffect(() => {
    setText(selectedProduto ? produtoLabel(selectedProduto) : '');
  }, [selectedProduto]);

  const sugestoes = (termo
    ? produtos.filter((produto) => produtoLabel(produto).toLowerCase().includes(termo))
    : produtos
  ).slice(0, 12);

  return (
    <div className="nx-autocomplete-filter">
      <div className="nx-search-field nx-search-field--filter nx-autocomplete-filter__control">
        <Search size={15} strokeWidth={2} aria-hidden />
        <input
          id={id}
          type="search"
          value={text}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            if (value) onChange('');
            setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
        />
        {text && !disabled ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setText('');
              onChange('');
            }}
            aria-label="Limpar produto"
          >
            ×
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <div className="nx-autocomplete-filter__menu">
          {sugestoes.length === 0 ? (
            <div className="nx-autocomplete-filter__empty">Nenhum produto encontrado</div>
          ) : (
            sugestoes.map((produto) => {
              const uuid = getUuidCandidate(produto) || '';
              return (
                <button
                  type="button"
                  key={produto.id}
                  className={uuid === value ? 'is-selected' : undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(uuid);
                    setText(produtoLabel(produto));
                    setOpen(false);
                  }}
                >
                  <strong>{produto.codigo || '-'}</strong>
                  <span>{produto.descricao}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function OrdemProducaoForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const visualizando = location.pathname.endsWith('/visualizar');
  const editando = Boolean(id) && !visualizando;
  const [form, setForm] = useState<OrdemProducaoInput>(FORM_VAZIO);
  const [detalhe, setDetalhe] = useState<OrdemProducao | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const [abaDetalhe, setAbaDetalhe] = useState<'materiais' | 'operacoes' | 'qualidade' | 'custos'>('materiais');
  const produtosValidos = produtos.filter((produto) => getUuidCandidate(produto));
  const responsaveisValidos = responsaveis.filter((responsavel) => getUuidCandidate(responsavel));

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
    responsavelService.listar().then(setResponsaveis).catch(console.error);
  }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    ordemProducaoService.buscarPorId(id).then((data) => {
      setDetalhe(data);
      setForm({
        numero: data.numero,
        produto_id: data.produto_id,
        quantidade_planejada: data.quantidade_planejada,
        quantidade_produzida: data.quantidade_produzida,
        lote_producao: data.lote_producao || '',
        data_inicio_planejada: data.data_inicio_planejada || '',
        data_fim_planejada: data.data_fim_planejada || '',
        responsavel_id: data.responsavel_id || '',
        observacoes: data.observacoes || '',
      });
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (visualizando) return;
    const errosFormulario: string[] = [];
    if (!form.numero?.trim()) errosFormulario.push('Número: informe o número da ordem de produção.');
    if (!isUuid(form.produto_id)) errosFormulario.push('Produto: selecione um produto válido na lista.');
    if (!form.quantidade_planejada || Number(form.quantidade_planejada) <= 0) errosFormulario.push('Quantidade planejada: informe um valor maior que zero.');
    if (form.responsavel_id && !isUuid(form.responsavel_id)) errosFormulario.push('Responsável: selecione um responsável válido.');

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      if (id) await ordemProducaoService.atualizar(id, form);
      else await ordemProducaoService.criar(form);
      navigate('/producao/ordens');
    } catch (error) {
      console.error('Erro ao salvar ordem de produção', error);
      setErros(extractErrorMessages(error));
    } finally {
      setSalvando(false);
    }
  };

  const title = visualizando ? 'Visualizar Ordem de Produção' : editando ? 'Editar Ordem de Produção' : 'Nova Ordem de Produção';
  const description = visualizando
    ? 'Consulte os dados, materiais, operações, qualidade e custos da ordem de produção.'
    : editando
      ? 'Atualize o planejamento da ordem de produção.'
      : 'Planeje produto, quantidade, lote e responsável.';

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<ClipboardCheck size={21} />} title={title} description={description} actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/ordens')}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível salvar a ordem de produção.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <fieldset disabled={loading || visualizando} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header">
              <h2>Dados da ordem</h2>
              {visualizando && detalhe ? <StatusBadge label={STATUS_LABELS[detalhe.status]} tone={STATUS_TONE[detalhe.status]} /> : null}
            </div>
            <div className="nx-sngpc-page-form-grid">
              <Field label="Número *" htmlFor="op-numero"><Input id="op-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} disabled={loading || visualizando} required /></Field>
              <Field label="Produto *" htmlFor="op-produto" className="nx-sngpc-field--full">
                <ProductAutocompleteField
                  id="op-produto"
                  produtos={produtosValidos}
                  value={form.produto_id}
                  onChange={(value) => setForm({ ...form, produto_id: value })}
                  disabled={loading || visualizando || editando}
                  required
                  placeholder="Buscar produto..."
                />
              </Field>
              <Field label="Quantidade planejada *" htmlFor="op-quantidade"><DecimalInput id="op-quantidade" value={form.quantidade_planejada} onValueChange={(value) => setForm({ ...form, quantidade_planejada: value ?? 0 })} disabled={loading || visualizando} required /></Field>
              {visualizando ? <Field label="Quantidade produzida" htmlFor="op-quantidade-produzida"><DecimalInput id="op-quantidade-produzida" value={form.quantidade_produzida ?? 0} onValueChange={() => {}} disabled /></Field> : null}
              <Field label="Lote produção" htmlFor="op-lote"><Input id="op-lote" value={form.lote_producao || ''} onChange={(e) => setForm({ ...form, lote_producao: e.target.value })} disabled={loading || visualizando} /></Field>
              <Field label="Início planejado" htmlFor="op-inicio"><Input id="op-inicio" type="date" value={form.data_inicio_planejada || ''} onChange={(e) => setForm({ ...form, data_inicio_planejada: e.target.value })} disabled={loading || visualizando} /></Field>
              <Field label="Fim planejado" htmlFor="op-fim"><Input id="op-fim" type="date" value={form.data_fim_planejada || ''} onChange={(e) => setForm({ ...form, data_fim_planejada: e.target.value })} disabled={loading || visualizando} /></Field>
              <Field label="Responsável" htmlFor="op-responsavel">
                <Select id="op-responsavel" value={form.responsavel_id || ''} onChange={(e) => setForm({ ...form, responsavel_id: e.target.value })} disabled={loading || visualizando}>
                  <option value="">Selecione</option>{responsaveisValidos.map((responsavel) => <option key={responsavel.id} value={getUuidCandidate(responsavel) || ''}>{responsavel.nome}</option>)}
                </Select>
              </Field>
              {visualizando ? (
                <>
                  <Field label="Início real" htmlFor="op-inicio-real"><Input id="op-inicio-real" value={formatDate(detalhe?.data_inicio_real)} disabled /></Field>
                  <Field label="Fim real" htmlFor="op-fim-real"><Input id="op-fim-real" value={formatDate(detalhe?.data_fim_real)} disabled /></Field>
                </>
              ) : null}
              <Field label="Observações" htmlFor="op-observacoes" className="nx-sngpc-field--full"><Textarea id="op-observacoes" value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} disabled={loading || visualizando} rows={3} /></Field>
            </div>
          </section>
        </fieldset>
      </Card>

      {visualizando && detalhe ? (
        <Card className="nx-page-card nx-sngpc-form-card">
          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header"><h2>Detalhes da produção</h2></div>
            <div className="nx-tabs">
              {[
                { id: 'materiais', label: 'Materiais' },
                { id: 'operacoes', label: 'Operações' },
                { id: 'qualidade', label: 'Qualidade' },
                { id: 'custos', label: 'Custos' },
              ].map((aba) => (
                <button
                  key={aba.id}
                  type="button"
                  className={abaDetalhe === aba.id ? 'nx-tab nx-tab--active' : 'nx-tab'}
                  onClick={() => setAbaDetalhe(aba.id as typeof abaDetalhe)}
                >
                  {aba.label}
                </button>
              ))}
            </div>

            {abaDetalhe === 'materiais' ? (
              <div className="nx-table-wrap">
                <table className="nx-table nx-sngpc-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Previsto</th>
                      <th>Consumido</th>
                      <th>Unidade</th>
                      <th>Variação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalhe.materiais || []).length === 0 ? (
                      <tr><td colSpan={5} className="nx-sngpc-empty-cell">Sem materiais vinculados.</td></tr>
                    ) : (
                      detalhe.materiais!.map((material) => (
                        <tr key={material.id}>
                          <td>{material.produto_nome} <span style={{ color: 'var(--nx-muted)' }}>({material.produto_codigo})</span></td>
                          <td>{formatNumber(material.quantidade_prevista)}</td>
                          <td>{formatNumber(material.quantidade_consumida)}</td>
                          <td>{material.unidade}</td>
                          <td>{formatNumber(material.variacao)}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}

            {abaDetalhe === 'operacoes' ? (
              <div className="nx-table-wrap">
                <table className="nx-table nx-sngpc-table">
                  <thead>
                    <tr>
                      <th>Seq</th>
                      <th>Operação</th>
                      <th>Centro de Trabalho</th>
                      <th>Tempo Previsto</th>
                      <th>Tempo Real</th>
                      <th>Concluída</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalhe.operacoes || []).length === 0 ? (
                      <tr><td colSpan={6} className="nx-sngpc-empty-cell">Sem operações vinculadas.</td></tr>
                    ) : (
                      detalhe.operacoes!.map((operacao) => (
                        <tr key={operacao.id}>
                          <td>{operacao.sequencia}</td>
                          <td>{operacao.operacao}</td>
                          <td>{operacao.centro_trabalho_nome}</td>
                          <td>{formatNumber(operacao.tempo_previsto)} min</td>
                          <td>{formatNumber(operacao.tempo_real)} min</td>
                          <td>{operacao.concluida ? 'Sim' : 'Não'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}

            {abaDetalhe === 'qualidade' ? (
              <div className="nx-table-wrap">
                <table className="nx-table nx-sngpc-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Resultado</th>
                      <th>Data</th>
                      <th>Responsável</th>
                      <th>Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalhe.qualidade || []).length === 0 ? (
                      <tr><td colSpan={5} className="nx-sngpc-empty-cell">Sem inspeções de qualidade.</td></tr>
                    ) : (
                      detalhe.qualidade!.map((qualidade) => (
                        <tr key={qualidade.id}>
                          <td>{qualidade.tipo}</td>
                          <td>{qualidade.resultado}</td>
                          <td>{formatDate(qualidade.data_inspecao)}</td>
                          <td>{qualidade.responsavel || '-'}</td>
                          <td>{qualidade.observacoes || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}

            {abaDetalhe === 'custos' ? (
              detalhe.custo ? (
                <div className="nx-table-wrap">
                  <table className="nx-table nx-sngpc-table">
                    <thead>
                      <tr>
                        <th>Categoria</th>
                        <th>Previsto</th>
                        <th>Real</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Matéria-Prima</td>
                        <td>{formatCurrency(detalhe.custo.custo_mp_previsto)}</td>
                        <td>{formatCurrency(detalhe.custo.custo_mp_real)}</td>
                      </tr>
                      <tr>
                        <td>Mão de Obra</td>
                        <td>{formatCurrency(detalhe.custo.custo_mdo_previsto)}</td>
                        <td>{formatCurrency(detalhe.custo.custo_mdo_real)}</td>
                      </tr>
                      <tr>
                        <td>Custos Indiretos</td>
                        <td>{formatCurrency(detalhe.custo.custo_indiretos_previsto)}</td>
                        <td>{formatCurrency(detalhe.custo.custo_indiretos_real)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState message="Custos ainda não calculados." />
              )
            ) : null}
          </section>
        </Card>
      ) : null}

      {visualizando ? (
        <FormStickyFooter>
          <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Button type="button" variant="ghost" onClick={() => navigate('/producao/ordens')}>
              <ArrowLeft size={16} />
              Voltar
            </Button>
            {id ? (
              <Button type="button" onClick={() => navigate(`/producao/ordens/${id}/editar`)}>
                <Pencil size={16} />
                Editar
              </Button>
            ) : null}
          </div>
        </FormStickyFooter>
      ) : (
        <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/ordens')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
      )}
    </form>
  );
}
